import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Captures the MediaStream that `react-html5-camera-photo` opens via getUserMedia and guarantees its
 * tracks are stopped when the camera is closed or the component unmounts — otherwise the browser keeps
 * the camera hardware on (Chrome's "camera in use" indicator / the device camera light stays lit until
 * the page is closed).
 *
 * Usage: pass the same `showCamera` boolean that controls the camera modal's visibility, and wire the
 * returned `handleCameraStart` into `<Camera onCameraStart={...} />` (its public callback hands us the
 * live stream). The stream is then stopped automatically when `showCamera` turns false or on unmount.
 */
export default function useCameraStream(showCamera: boolean) {
  const streamRef = useRef<MediaStream | null>(null);
  const showCameraRef = useRef(showCamera);
  showCameraRef.current = showCamera; // keep the latest value for the async onCameraStart callback

  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleCameraStart = useCallback(
    (stream: MediaStream) => {
      // Rapid open -> close race: the camera was already dismissed before getUserMedia resolved.
      if (!showCameraRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      stopCameraStream(); // stop any previous stream (e.g. the facing-mode `key` remount)
      streamRef.current = stream;
    },
    [stopCameraStream]
  );

  // Stop the stream as soon as the camera is hidden.
  useEffect(() => {
    if (!showCamera) {
      stopCameraStream();
    }
  }, [showCamera, stopCameraStream]);

  // Safety net: stop the stream if the component unmounts while the camera is still open.
  useLayoutEffect(() => stopCameraStream, [stopCameraStream]);

  return { handleCameraStart, stopCameraStream };
}
